import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, SyntheticEvent } from 'react'

import { IconButton } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import RoundAddButtonMobile from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/icons/roundAddButton.inline.svg'
import { RECOMMENDATION_TRACKING_KEYS } from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/molecules/cartPopupTwo/molecules/recommendedForYou/molecules/product-list-carousel/product-list-carousel.constants'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { handleTrackAddedToCartAlgolia } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import RoundAddButtonDesktop from '../../icons/roundAddButton.inline.svg'
import { getSelectedOptionsForProductCard } from '../../product-card.helpers'
import { CardVariant, IProductCard } from '../../product-card.types'

import styles from './buy-it-now.module.scss'

// @i18n-ignore
const CHECKOUT = 'checkout'
// @i18n-ignore
const BUY_NOW_DATA_TEST_ID = 'buy-now'

export const ProductCardBuyItNow: FC<{
  productCard: IProductCard
  selectedCardVariant?: CardVariant
  addToCartClassName?: string
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId?: string
  productClickTrackConfig?: TrackConfig
  quickBuyOverride?: () => void
  tabData?: Record<string, string | boolean>
}> = ({
  productCard,
  selectedCardVariant,
  addToCartClassName,
  siteSearchQuery,
  productPosition,
  productSearchObjectId,
  productClickTrackConfig,
  quickBuyOverride,
  tabData,
}) => {
  const { t } = useTranslation('common')
  const searchAlgoliaQueryId =
    useAppSelector<SearchDetails>(siteStateManager, siteStateManager.getSearchDetails).searchAlgoliaQueryId || ''
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const RoundAddButton = isMobile ? RoundAddButtonMobile : RoundAddButtonDesktop

  const handleAddToCartClick = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const product = productCard.product
    const productProperties = [
      RECOMMENDATION_TRACKING_KEYS.REC_MODULE_ID,
      RECOMMENDATION_TRACKING_KEYS.IS_CART_MODAL,
      RECOMMENDATION_TRACKING_KEYS.REC_ELEMENT_NAME,
      RECOMMENDATION_TRACKING_KEYS.REC_PRODUCT_COUNT,
    ].reduce<Record<string, string | boolean>>((acc, key) => {
      const value = tabData?.[key]
      if (value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})
    const buyNowClickTrackConfig: TrackConfig = {
      trackGA: true,
      trackInternal: true,
      category: CHECKOUT,
      // @i18n-ignore
      action: 'click_quick_shop',
      context: {
        ...productClickTrackConfig?.context,
        product_id: product.relatedIds.spid,
        internalProductId: `${product.productId}_${selectedCardVariant?.displayConfig.value}`,
        product_search_query: siteSearchQuery,
        product_position: productPosition,
        product_price: selectedCardVariant?.price?.salePrice,
        name: product.title,
        color_name: selectedCardVariant?.displayConfig.value,
        color_variant: product.colorVariants,
        rating: product.reviews.averageRating,
        productProperties,
        custom_variants: product.customVariants,
        oos_flag: getIndividualTagStatus((selectedCardVariant?.tags ?? []).map(item => item.value as ProductTagKey))
          .sold_out,
      },
    }

    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, buyNowClickTrackConfig)

    handleTrackAddedToCartAlgolia({
      objectID: productSearchObjectId ?? '',
      queryID: searchAlgoliaQueryId,
    })

    // zero price products don't trigger buy now popup
    if (!selectedCardVariant?.price?.salePrice) return

    if (quickBuyOverride) {
      quickBuyOverride()
      return
    }

    productBuyNowStateManager.showBuyNowPopUp({
      productCard: productCard,
      selectedOptions: getSelectedOptionsForProductCard(selectedCardVariant),
      searchQuery: siteSearchQuery,
      position: productPosition,
      productClickTrackConfig: productClickTrackConfig,
      tabData: tabData,
    })
  }

  const productTitle = productCard.product.title
  const colorName = selectedCardVariant?.displayConfig.displayName
  const productName = colorName ? `${colorName} ${productTitle}` : productTitle
  const price = selectedCardVariant?.price?.salePrice
  const formattedPrice = price ? price.toFixed(2) : null
  const buyNowAriaLabel = formattedPrice
    ? t('pc_buy_now_price_aria', { productName, price: formattedPrice })
    : t('pc_buy_now_aria', { productName })

  return (
    <IconButton
      className={classnames(styles.addToCart, addToCartClassName)}
      iconClassName={styles.icon}
      onClick={handleAddToCartClick}
      iconSvg={RoundAddButton}
      ariaLabel={buyNowAriaLabel}
      dataTestId={BUY_NOW_DATA_TEST_ID}
    />
  )
}
